import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { getProductById } from '../services/productService';
import { useCart } from '../context/CartContext';
import { useLanguage } from '../context/LanguageContext';
import { translateText } from '../services/translationService';
import { formatPrice, getConvertedPrice } from '../services/currencyService';
import { motion } from 'framer-motion';

export default function ProductDetail() {
  const { id } = useParams();
  const { addToCart } = useCart();
  const { language } = useLanguage();
  const navigate = useNavigate();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [translatedTexts, setTranslatedTexts] = useState({});
  const [translating, setTranslating] = useState(false);

  // Configuration des URLs
  const API_BASE_URL = process.env.NODE_ENV === 'development'
    ? 'http://localhost/argan-shop'
    : 'https://d4unaturally.com';
  const REACT_BASE_URL = process.env.NODE_ENV === 'development'
    ? 'http://localhost:3000'
    : (process.env.PUBLIC_URL || '');

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        setError(null);
        const productData = await getProductById(id);
        setProduct(productData);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    if (id) fetchProduct();
    else setError('Product ID missing');
  }, [id]);

  // 🌐 Traduction de l'interface utilisateur
  useEffect(() => {
    const translateUI = async () => {
      const textsToTranslate = {
        backToProducts: "Back to products",
        loading: "Loading product",
        error: "Error",
        returnToShop: "Return to shop",
        inStock: "In stock",
        outOfStock: "Out of stock",
        quantity: "Quantity",
        howToUse: "How to use",
        addToCart: "Add to cart",
        buyNow: "Buy now",
        priceNotAvailable: "Price not available",
        translating: "Updating translation..."
      };

      const translated = {};
      for (const [key, value] of Object.entries(textsToTranslate)) {
        translated[key] = await translateText(value, language);
      }
      setTranslatedTexts(translated);
    };

    if (!loading) {
      translateUI();
    }
  }, [language, loading]);

  // 🧠 Traduction du produit
  useEffect(() => {
    const translateProduct = async () => {
      if (!product || language === 'fr') return;

      try {
        setTranslating(true);

        // Traduire les champs du produit
        const [translatedName, translatedDescription, translatedHowToUse] = await Promise.all([
          translateText(product.name, language),
          product.description ? translateText(product.description, language) : Promise.resolve(product.description),
          product.how_to_use ? translateText(product.how_to_use, language) : Promise.resolve(product.how_to_use)
        ]);

        setProduct(prevProduct => ({
          ...prevProduct,
          name: translatedName,
          description: translatedDescription,
          how_to_use: translatedHowToUse
        }));

      } catch (error) {
        console.error("Error translating product:", error);
      } finally {
        setTranslating(false);
      }
    };

    if (product) {
      translateProduct();
    }
  }, [language, product?.id]);

  // 🖼️ MÉTHODE UNIFIÉE pour les images
  const getProductImages = (product) => {
    if (!product) return [`${REACT_BASE_URL}/images/placeholder.jpg`];

    // Essayer d'abord la nouvelle méthode (uploads API)
    const newMethodImages = getProductImagesNew(product);

    // Vérifier si les images de la nouvelle méthode existent
    if (newMethodImages && newMethodImages.length > 0 &&
      !newMethodImages[0].includes('default-product.jpg') &&
      !newMethodImages[0].includes('placeholder.jpg')) {
      return newMethodImages;
    }

    // Sinon, essayer l'ancienne méthode
    const oldMethodImages = getProductImagesOld(product);
    if (oldMethodImages && oldMethodImages.length > 0 &&
      !oldMethodImages[0].includes('placeholder.jpg')) {
      return oldMethodImages;
    }

    // Si aucune méthode ne fonctionne, retourner l'image par défaut
    return [`${API_BASE_URL}/images/default-product.jpg`];
  };

  // 🖼️ MÉTHODE 1 - Pour les anciens produits
  const getProductImagesOld = (product) => {
    const baseUrl = process.env.NODE_ENV === 'development'
      ? 'http://localhost:3000'
      : (process.env.PUBLIC_URL || '');

    if (!product) return [`${baseUrl}/images/placeholder.jpg`];

    if (product.image_url) {
      return [product.image_url.startsWith('http')
        ? product.image_url
        : `${baseUrl}/images/products/${product.image_url.replace(/^\/+/, '')}`
      ];
    }

    if (product.images) {
      try {
        const imagesArray = JSON.parse(product.images.replace(/\\"/g, '"'));
        if (imagesArray.length > 0) {
          return imagesArray.map(img =>
            `${baseUrl}/images/products/${img.replace(/^\/+/, '').trim()}`
          );
        }
      } catch (error) {
        console.error("Error parsing images:", error);
      }
    }

    if (product.image) {
      return [`${baseUrl}/images/products/${product.image.replace(/^\/+/, '')}`];
    }

    return [`${baseUrl}/images/placeholder.jpg`];
  };

  // 🖼️ MÉTHODE 2 - Pour les nouveaux produits
  const getProductImagesNew = (product) => {
    if (!product) return [`${REACT_BASE_URL}/images/placeholder.jpg`];

    if (product.image && product.image.startsWith('http')) {
      return [product.image];
    }

    if (product.image) {
      return [`${API_BASE_URL}${product.image}`];
    }

    if (product.image_url) {
      return [product.image_url.startsWith('http')
        ? product.image_url
        : `${API_BASE_URL}/images/products/${product.image_url.replace(/^\/+/, '')}`
      ];
    }

    if (product.images) {
      try {
        const imagesArray = JSON.parse(product.images.replace(/\\"/g, '"'));
        if (imagesArray.length > 0) {
          return imagesArray.map(img =>
            img.startsWith('http')
              ? img
              : `${API_BASE_URL}/images/products/${img.replace(/^\/+/, '').trim()}`
          );
        }
      } catch (error) {
        console.error("Error parsing images:", error);
      }
    }

    return [`${API_BASE_URL}/images/default-product.jpg`];
  };

  // 🛒 Ajouter au panier
  const handleAddToCart = () => {
    if (product) {
      const productImages = getProductImages(product);

      addToCart({
        id: product.id,
        name: product.name,
        price: parseFloat(product.price), // Prix original en MAD
        image: productImages[0],
        quantity: quantity
      });

      const message = `${product.name} ${translatedTexts.addToCart ? translatedTexts.addToCart.toLowerCase() : 'added to cart'}!`;
      alert(message);
    }
  };

  // 🚀 ACHAT IMMÉDIAT - CORRECTION DU BOUTON BUY NOW
  const handleBuyNow = () => {
    if (product) {
      const productImages = getProductImages(product);

      // Ajouter au panier
      addToCart({
        id: product.id,
        name: product.name,
        price: parseFloat(product.price), // Prix original en MAD
        image: productImages[0],
        quantity: quantity
      });

      // Rediriger immédiatement vers le panier
      navigate('/cart');
    }
  };

  // 🔄 État de chargement
  if (loading) {
    return (
      <div className="min-h-screen bg-[#fffaf3] flex justify-center items-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#d0b125] mx-auto mb-4"></div>
          <p className="text-[#d0b125] text-lg font-light">
            {translatedTexts.loading || "Loading product"} {id}...
          </p>
        </div>
      </div>
    );
  }

  // ❌ État d'erreur
  if (error) {
    return (
      <div className="min-h-screen bg-[#fffaf3] flex justify-center items-center">
        <div className="text-center p-8">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md mx-auto">
            <h2 className="text-red-800 text-xl font-semibold mb-2">
              {translatedTexts.error || "Error"}
            </h2>
            <p className="text-red-600 mb-4">{error}</p>
            <Link
              to="/shop"
              className="bg-[#d0b125] text-white px-6 py-2 rounded-lg hover:bg-[#b89a20] transition"
            >
              {translatedTexts.returnToShop || "Return to shop"}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!product) return null;

  // Utiliser la méthode unifiée pour obtenir les images
  const images = getProductImages(product);

  // 💰 FORMATER LE PRIX AVEC CONVERSION - CORRECTION
  const displayPrice = product.price
    ? formatPrice(product.price, language, 'MAD') // Prix original en MAD, converti selon langue
    : translatedTexts.priceNotAvailable || 'Price not available';

  return (
    <div className="min-h-screen bg-[#fffaf3] py-12">
      {/* --- Overlay de traduction --- */}
      {translating && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 flex items-center space-x-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#d0b125]"></div>
            <p className="text-[#2f2a1e] font-light">
              {translatedTexts.translating || "Updating translation..."}
            </p>
          </div>
        </div>
      )}

      <div className="max-w-6xl mx-auto px-6">
        {/* Navigation */}
        <nav className="mb-6 text-sm">
          <Link
            to="/shop"
            className="text-[#7a7250] hover:text-[#d0b125] transition"
          >
            ← {translatedTexts.backToProducts || "Back to products"}
          </Link>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Images - Carrousel */}
          <div className="space-y-4">
            <div className="relative bg-white border border-[#e8e2d1] rounded-xl shadow-lg overflow-hidden group">
              <motion.img
                src={images[currentImageIndex]}
                alt={product.name}
                className="w-full h-96 object-cover rounded-lg cursor-zoom-in"
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.3 }}
                onError={(e) => {
                  const fallbackImages = getProductImagesOld(product);
                  if (fallbackImages && fallbackImages[0] !== images[currentImageIndex]) {
                    e.target.src = fallbackImages[0];
                  } else {
                    e.target.src = `${API_BASE_URL}/images/default-product.jpg`;
                  }
                }}
              />

              {/* Indicateurs de navigation pour le carrousel */}
              {images.length > 1 && (
                <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 flex space-x-2 bg-white bg-opacity-60 rounded-md px-2 py-1">
                  {images.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentImageIndex(index)}
                      className={`w-3 h-3 rounded-full transition-all ${currentImageIndex === index
                          ? 'bg-[#d0b125]'
                          : 'bg-gray-300'
                        }`}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Miniatures des images */}
            {images.length > 1 && (
              <div className="flex space-x-2 overflow-x-auto py-2">
                {images.map((image, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentImageIndex(index)}
                    className={`flex-shrink-0 w-16 h-16 border-2 rounded-lg overflow-hidden transition-all ${currentImageIndex === index
                        ? 'border-[#d0b125]'
                        : 'border-transparent'
                      }`}
                  >
                    <img
                      src={image}
                      alt={`${product.name} ${index + 1}`}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        const fallbackImages = getProductImagesOld(product);
                        if (fallbackImages && fallbackImages[index] !== image) {
                          e.target.src = fallbackImages[index] || `${API_BASE_URL}/images/default-product.jpg`;
                        } else {
                          e.target.src = `${API_BASE_URL}/images/default-product.jpg`;
                        }
                      }}
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Informations du produit */}
          <div className="space-y-6">
            {/* Nom du produit */}
            <h1 className="text-3xl lg:text-4xl font-serif font-semibold text-[#2f2a1e]">
              {product.name}
            </h1>

            {/* Prix et statut de stock - AVEC CONVERSION */}
            <div className="flex items-center space-x-4">
              <p className="text-2xl lg:text-3xl text-[#d0b125] font-bold">
                {displayPrice}
              </p>
              {product.in_stock ? (
                <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-xs font-medium">
                  {translatedTexts.inStock || "In stock"}
                </span>
              ) : (
                <span className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-xs font-medium">
                  {translatedTexts.outOfStock || "Out of stock"}
                </span>
              )}
            </div>

            {/* Description */}
            {product.description && (
              <div className="prose max-w-none">
                <p className="text-[#7a7250] leading-relaxed">
                  {product.description}
                </p>
              </div>
            )}

            {/* Mode d'emploi */}
            {product.how_to_use && (
              <div className="bg-[#f8f5f0] border border-[#e8e2d1] rounded-lg p-4">
                <h3 className="font-semibold text-[#2f2a1e] mb-2">
                  {translatedTexts.howToUse || "How to use"}
                </h3>
                <p className="text-[#7a7250] text-sm leading-relaxed">
                  {product.how_to_use}
                </p>
              </div>
            )}

            {/* Sélecteur de quantité */}
            <div className="flex items-center space-x-4">
              <label className="text-[#2f2a1e] font-medium">
                {translatedTexts.quantity || "Quantity"}
              </label>
              <div className="flex items-center border border-[#e8e2d1] rounded-md">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="px-3 py-2 hover:bg-[#f5f1eb] transition text-[#2f2a1e]"
                  disabled={quantity <= 1}
                >
                  -
                </button>
                <span className="px-4 py-2 text-[#2f2a1e] min-w-[3rem] text-center">
                  {quantity}
                </span>
                <button
                  onClick={() => setQuantity(quantity + 1)}
                  className="px-3 py-2 hover:bg-[#f5f1eb] transition text-[#2f2a1e]"
                >
                  +
                </button>
              </div>
            </div>

            {/* Boutons d'action - BOUTON BUY NOW ACTIVÉ */}
            <div className="space-y-3">
              <button
                onClick={handleAddToCart}
                disabled={!product.in_stock}
                className={`w-full py-3 rounded-lg font-semibold transition-all ${product.in_stock
                    ? 'bg-[#2f2a1e] text-[#fffaf3] hover:bg-[#d0b125]'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
              >
                {product.in_stock
                  ? (translatedTexts.addToCart || "ADD TO CART")
                  : (translatedTexts.outOfStock || "OUT OF STOCK")
                }
              </button>

              {/* BOUTON BUY NOW CORRIGÉ - MAINTENANT FONCTIONNEL */}
              <button
                onClick={handleBuyNow} // ✅ CORRECTION : Appel de la fonction handleBuyNow
                disabled={!product.in_stock}
                className={`w-full border-2 py-3 rounded-lg font-semibold transition-all ${product.in_stock
                    ? 'border-[#2f2a1e] text-[#2f2a1e] hover:bg-[#2f2a1e] hover:text-[#fffaf3]'
                    : 'border-gray-300 text-gray-400 cursor-not-allowed'
                  }`}
              >
                {translatedTexts.buyNow || "BUY NOW"}
              </button>
            </div>

            {/* Informations supplémentaires */}
            <div className="border-t border-[#e8e2d1] pt-4">
              <div className="grid grid-cols-2 gap-4 text-sm text-[#7a7250]">
                {product.sku && (
                  <div>
                    <span className="font-medium">SKU:</span> {product.sku}
                  </div>
                )}
                {product.category && (
                  <div>
                    <span className="font-medium">Category:</span> {product.category}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}